import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Droplet,
  Activity,
  Zap,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Sparkles,
  Loader2,
  CheckCircle2
} from 'lucide-react';

import { API_URL } from '../config';

export default function ReferentielsMetaboliquePage() {
  const [aiLoading, setAiLoading] = useState({});
  const [aiResults, setAiResults] = useState({});

  const handleAiAnalysis = async (pathoId, pathoName, content) => {
    setAiLoading({ ...aiLoading, [pathoId]: true });
    
    try {
      const response = await fetch(`${API_URL}/api/ai/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          context: 'referentiels_metabolique',
          audience: 'adult',
          prompt: `Analyse métabolique pour ${pathoName}:

${content.slice(0, 500)}

Fournis une analyse synthétique avec:
1. Mécanisme physiopathologique
2. Signes ECG caractéristiques à rechercher
3. Traitement en urgence avec posologies
4. Critères de gravité nécessitant réanimation
5. Surveillance biologique essentielle`
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAiResults({ ...aiResults, [pathoId]: data.response });
        toast.success(`Analyse AI ${pathoName} complétée`);
      } else {
        toast.error('Erreur lors de l\'analyse');
      }
    } catch (error) {
      console.error('AI Analysis error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setAiLoading({ ...aiLoading, [pathoId]: false });
    }
  };

  const pathologies = [
    {
      id: 'hyperkaliemie',
      name: 'Hyperkaliémie',
      icon: TrendingUp,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      border: 'border-red-400/30',
      content: `**HYPERKALIÉMIE**

**DÉFINITION:**
• Kaliémie > 5,5 mmol/L

**ÉTIOLOGIES PRÉ-HOSPITALIÈRES:**
• Acidose métabolique
• Rhabdomyolyse, crush syndrome
• Brûlures étendues
• Iatrogénie:
  - Bêtabloquants
  - Intoxication digitalique
  - Apport excessif de potassium
• Hypothermie
• Insuffisance rénale (aiguë ou chronique)

**SIGNES CLINIQUES:**

**NEUROMUSCULAIRES (non spécifiques):**
• Paresthésies des extrémités
• Faiblesse musculaire
• Paralysie des muscles lisses:
  - Atonie intestinale
  - Constipation
• Aréflexie ostéotendineuse
• Rhabdomyolyse

**DIGESTIFS ET URINAIRES:**
• Constipation
• Iléus paralytique
• Rétention aiguë d'urine

**SIGNES ECG - EXAMEN CLÉ:**

**Stade 1 - Onde T:**
• Ondes T amples, pointues, symétriques

**Stade 2 - Conduction auriculaire:**
• Diminution durée onde P
• Disparition onde P
• Troubles de conduction AV (BAV)

**Stade 3 - Conduction intraventriculaire:**
• Élargissement QRS

**Stade 4 - Arythmies ventriculaires:**
• Tachycardie ventriculaire
• Fibrillation ventriculaire
• Arrêt cardiaque

**⚠️ URGENCE ABSOLUE:**
Signes ECG d'hyperkaliémie = Risque vital imminent

**TRAITEMENT:**

**MONITORING:**
• Scope ECG continu obligatoire

**1. CALCIUM (Antagoniste membranaire):**
• **Chlorure de calcium 10%:**
  - 10 mL dilué dans 50 mL G5%
  - IV sur 8-10 minutes
• **⚠️ CONTRE-INDICATION:**
  - Si suspicion intoxication digitalique

**2. RÉSINES (Captation digestive):**
• **Kayexalate®:**
  - 2 cuillères-mesure
  - Per os ou rectal

**3. INSULINE-GLUCOSE (Transfert intracellulaire):**
• **Insuline rapide:** 10 UI
• Diluée dans 250 mL G10%
• IV sur 15-30 minutes
• **Surveillance glycémique stricte**

**4. SALBUTAMOL (β2-agoniste):**
• 5-10 mg en nébulisation

**5. BICARBONATE 4,2% (Si acidose):**
• **Bicarbonate semi-molaire:**
  - 2 mL/kg sur 10 minutes
• **⚠️ CONTRE-INDICATION:**
  - Surcharge hydrosodée

**6. ÉPURATION EXTRA-RÉNALE:**
• Selon réponse thérapeutique
• Étiologie
• Évolution

**SURVEILLANCE:**
• ECG continu
• Kaliémie répétée
• Ionogramme complet
• Fonction rénale
• Gazométrie

**ORIENTATION:**
• Transport médicalisé
• Réanimation ou USI
• Néphrologue si dialyse nécessaire`
    },
    {
      id: 'hypokaliemie',
      name: 'Hypokaliémie',
      icon: TrendingDown,
      color: 'text-blue-400',
      bg: 'bg-blue-500/10',
      border: 'border-blue-400/30',
      content: `**HYPOKALIÉMIE**

**DÉFINITION:**
• Kaliémie < 3,5 mmol/L

**ÉTIOLOGIES PRÉ-HOSPITALIÈRES:**

**APPORT INSUFFISANT:**
• Anorexie
• Nutrition artificielle

**ALCALOSE:**
• Métabolique ou respiratoire

**PERTES DIGESTIVES:**
• Diarrhée (aiguë ou chronique)
• Fistules digestives

**PERTES RÉNALES:**
• Polyurie
• Hypermineralocorticisme
• Hyperglucocorticisme
• Néphropathies avec perte de sel
• Diurétiques
• Vomissements importants

**SIGNES CLINIQUES:**

**NEUROMUSCULAIRES (non spécifiques):**
• Crampes
• Myalgies
• Faiblesse musculaire
• Paralysie muscles lisses:
  - Iléus
  - Constipation
• Aréflexie ostéotendineuse
• Rhabdomyolyse

**DIGESTIFS ET URINAIRES:**
• Constipation
• Iléus paralytique
• Rétention aiguë d'urine

**SIGNES ECG - EXAMEN CLÉ:**

**Stade 1:**
• Onde T aplatie ou inversée
• PR allongé

**Stade 2:**
• Apparition onde U pathologique

**Stade 3:**
• QT allongé
• QRS élargi

**Stade 4:**
• Arythmies supraventriculaires
• Arythmies ventriculaires
• Arrêt cardiaque

**CONDUITE À TENIR:**

**MONITORING:**
• Scope ECG continu obligatoire

**SI PAS D'ANOMALIE ECG ET K+ PAS SÉVÈRE:**
• **KCl per os:**
  - 4-8 g/jour
  - Surveillance régulière

**SI ANOMALIES ECG OU K+ < 2,5 mmol/L:**

**Objectif:** Kaliémie > 3 mmol/L

**VOIE VEINEUSE PÉRIPHÉRIQUE:**
• **KCl IV:** 0,5-1 g/h maximum
• Exemple: 2g KCl dans 50 mL NaCl
  - Sur 4 heures IVSE
• **⚠️ JAMAIS EN BOLUS**

**VOIE VEINEUSE CENTRALE:**
• **KCl IVSE:** 1 g/h

**SI TORSADES DE POINTES:**
• **Sulfate de magnésium:**
  - 2 g IVSE sur 10 minutes

**ALTERNATIVE:**
• **Magnésium:** 6-8 g/24h
• Selon magnésémie

**⚠️ PRÉCAUTIONS:**
• **JAMAIS de bolus de KCl** (risque ACR)
• Dilution obligatoire
• Débit strict
• Surveillance continue ECG
• Contrôle kaliémie régulier

**SURVEILLANCE:**
• ECG continu
• Kaliémie toutes les 2-4h
• Magnésémie
• Fonction rénale
• Recherche étiologie

**ORIENTATION:**
• Transport médicalisé si sévère
• Hospitalisation si K+ < 2,5 mmol/L
• USI/Réanimation si troubles du rythme`
    },
    {
      id: 'acidocetose-adulte',
      name: 'Acidocétose Diabétique (Adulte)',
      icon: Droplet,
      color: 'text-yellow-400',
      bg: 'bg-yellow-500/10',
      border: 'border-yellow-400/30',
      content: `**ACIDOCÉTOSE DIABÉTIQUE - ADULTE**

**CONVERSION:**
• Glycémie (mmol/L) = Glycémie (g/L) x 5,5

**DIAGNOSTIC CLINIQUE:**
• Déshydratation
• Polyurie, polydipsie
• Nausées, vomissements
• Douleurs abdominales
• Fièvre
• **Respiration de Kussmaul:**
  - Ample et rapide
  - Inspiration, pause
  - Expiration forcée, pause
• Altération conscience

**DIAGNOSTIC BIOLOGIQUE:**

**HYPERGLYCÉMIE:**
• Veineuse ou capillaire

**ACIDOSE MÉTABOLIQUE:**
• pH < 7,38
• HCO3- < 20 mmol/L

**CÉTONÉMIE CAPILLAIRE:**
• **Critère fort:** > 3 mmol/L + hyperglycémie
• Si indisponible: cétonurie (moins sensible)

**ÉTIOLOGIES:**
• Diabète inaugural (30-40%)
• Infection (tout site/gravité)
• Non-observance (adolescents++)
• Affections intercurrentes:
  - Ischémie myocardique
  - AVC
  - Pancréatite aiguë
  - Traumatisme
• Iatrogénie:
  - Corticoïdes
  - Alcool

**CRITÈRES DE GRAVITÉ:**

**CLINIQUES:**
• Altération conscience
• Choc hypovolémique

**BIOLOGIQUES:**
• Glycémie > 14 mmol/L
• pH < 7,0
• Bicarbonatémie < 10 mmol/L
• Cétonémie > 6 mmol/L

**TRAITEMENT:**

**MISE EN PLACE:**
• 2 VVP
• Monitoring
• Glycémie et cétonémie capillaires horaires

**1. RÉHYDRATATION:**

**Initiale:**
• **1 L NaCl 0,9%** en débit libre

**Entretien:**
• **1 L/h NaCl 0,9%** ou soluté équilibré
  - (Isofundine, Ringer Lactate)
• Réévaluation à 4h

**Ajustements selon glycémie:**
• **Si glycémie < 11 mmol/L:**
  - Ajouter G5% au NaCl 0,9%
• **Si glycémie < 6 mmol/L:**
  - Ajouter G10%
• **Poursuivre G5%** jusqu'à disparition cétones

**2. INSULINOTHÉRAPIE:**
(Après contrôle kaliémie)

• **IVSE continue:**
  - 0,1 UI/kg/h (max 10 UI/h)
• **Si baisse glycémie < 3 mmol/L/h:**
  - Doubler le débit
• **Si glycémie < 11 mmol/L:**
  - Supplémenter en glucose

**3. POTASSIUM:**

**Surveillance fréquente obligatoire**

**Si K+ < 3,3 mmol/L:**
• **ARRÊTER insuline**
• Substitution potassique:
  - Voie centrale: 0,5-1 g/h
  - OU 2 VVP périphériques
  - Exemple: 2g KCl dans 50 mL NaCl sur 4h
  - **⚠️ JAMAIS EN BOLUS**
• **Objectif:** K+ > 3,3 mmol/L

**Si K+ 3,3-5 mmol/L:**
• Administrer KCl avec insuline

**Si K+ > 5 mmol/L:**
• Arrêter KCl

**OBJECTIFS DE CORRECTION:**
• **Cétonémie:** baisse 0,5 mmol/L/h
• **OU Bicarbonates:** hausse 3 mmol/L/h
• **Glycémie capillaire:** baisse 3 mmol/L/h

**CAS SÉVÈRES (USC/Réanimation):**
• Glasgow < 12
• PAS < 90 mmHg
• SpO2 < 92%
• Cétonémie > 6 mmol/L
• Bicarbonatémie < 5 mmol/L
• pH artériel/veineux < 7,10
• Kaliémie admission < 3,5 mmol/L
• Trou anionique > 16 mmol/L
• Comorbidités

**SURVEILLANCE:**
• Glycémie capillaire horaire
• Cétonémie capillaire horaire
• Kaliémie toutes les 2-4h
• Ionogramme complet
• Gazométrie
• État neurologique
• Diurèse

**⚠️ COMPLICATION - ŒDÈME CÉRÉBRAL:**
(Rare mais grave)

**Signes:**
• Céphalées, vomissements
• Troubles de conscience
• HTA, bradycardie

**Traitement:**
• Mannitol 0,5-1 g/kg IV
• OU SSH 3%

**ORIENTATION:**
• Transport médicalisé
• Réanimation si critères sévères
• USC sinon`
    },
    {
      id: 'insuffisance-surrenalienne',
      name: 'Insuffisance Surrénalienne Aiguë',
      icon: AlertTriangle,
      color: 'text-orange-400',
      bg: 'bg-orange-500/10',
      border: 'border-orange-400/30',
      content: `**INSUFFISANCE SURRÉNALIENNE AIGUË**

**DIAGNOSTIC:**

**PENSER SYSTÉMATIQUEMENT:**
• Patient avec insuffisance surrénalienne connue
• ATCD corticothérapie au long cours

**PRÉSENTATION CLINIQUE NON SPÉCIFIQUE:**

**DIGESTIFS:**
• Douleurs abdominales diffuses
• Tableau pseudo-chirurgical
• Diarrhée

**NEUROLOGIQUES:**
• Asthénie profonde
• OU confusion, agitation
• Altération conscience +/- coma

**CARDIOVASCULAIRES:**
• Hypotension artérielle
• Hypotension orthostatique
• Tachycardie
• Déshydratation
• Signes de choc

**GÉNÉRAUX:**
• Hyperthermie
• Pâleur
• Sueurs

**NEUROMUSCULAIRES:**
• Crampes
• Pseudo-paralysie

**CAUSES DE DÉCOMPENSATION:**

**1ère CAUSE:**
• Diarrhées et vomissements
  - Chez insuffisant surrénalien connu

**2ème CAUSE:**
• Infections bactériennes ou virales

**AUTRES:**
• Stress professionnel ou personnel
• Douleur intense
• Accouchement
• Chirurgie
• Exercice physique intense
• Médicaments inducteurs enzymatiques:
  - Phénobarbital
  - Phénytoïne
  - Carbamazépine
  - Rifampicine
• Arrêt brutal corticothérapie au long cours

**BIOLOGIE:**

**PRÉLÈVEMENTS AVANT TRAITEMENT:**
• **Cortisol** (tube rouge)
• **ACTH** (tube EDTA)
• ⚠️ Important pour diagnostic ultérieur
• **Ne pas retarder traitement**

**CRITÈRES DE GRAVITÉ:**

**CLINIQUES:**
• Instabilité hémodynamique
• Signes de choc

**VOMISSEMENTS:**
• > 2 épisodes en 4 heures
• Douleurs abdominales sévères
• Diarrhée importante

**NEUROLOGIQUES:**
• Convulsions
• Altération conscience (Glasgow < 14)

**AUTRES:**
• Sepsis sévère
• Hypoglycémie < 0,4 g/L
• Hypokaliémie avec anomalies ECG

**CONDUITE À TENIR:**

**MESURES GÉNÉRALES:**
• 2 VVP
• Monitoring
• **Glycémie capillaire +++**

**INITIER TRAITEMENT IMMÉDIATEMENT:**
• **Ne pas attendre** résultats cortisol/ACTH

**TRAITEMENT SUBSTITUTIF:**

**HYDROCORTISONE HÉMISUCCINATE (HSHC):**

**ADULTE:**
• **Bolus initial:** 100 mg IM ou IVD
• **Entretien:**
  - IVSE continue: 100-200 mg/24h
  - OU bolus IV/IM: 50 mg toutes les 6h

**ENFANT:**
• 2 mg/kg toutes les 6h
• IM, SC ou IVD

**TRAITER LA CAUSE:**

**CORRECTION HYPOVOLÉMIE:**
• Débuter par 1 L NaCl sur 1h
• Puis réévaluation

**ENFANT:**
• 20 mL/kg sur 20 min
• Réévaluation

**CORRECTION HYPOGLYCÉMIE:**
• G30% selon protocole

**CORRECTION PERTES HYDRO-ÉLECTROLYTIQUES:**
• Selon bilan

**SURVEILLANCE:**
• État hémodynamique
• Conscience
• Glycémie régulière
• Ionogramme
• Réponse au traitement

**CRITÈRE DIAGNOSTIQUE:**
• **Évolution clinique favorable** après HSHC
• Si pas d'amélioration:
  - Rechercher autres étiologies

**ORIENTATION:**
• Transport médicalisé
• Selon évolution clinique
• SAU ou Réanimation
• Après régulation

**À SAVOIR:**
• Meilleur critère diagnostique = réponse favorable à HSHC
• Traitement ne peut pas attendre confirmation biologique
• Prélèvements avant traitement mais ne retardent pas prise en charge
• Suivi endocrinologique ultérieur nécessaire`
    }
  ];

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-xl border border-blue-400/30">
            <Activity className="w-8 h-8 text-blue-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-[hsl(var(--text))]">Référentiels Métabolique</h1>
            <p className="text-[hsl(var(--text-dim))]">Urgences métaboliques et hydroélectrolytiques - SMUR 2025</p>
          </div>
        </div>
        
        <Alert className="bg-blue-500/10 border-blue-400/30">
          <Zap className="w-4 h-4 text-blue-400" />
          <AlertDescription className="text-[hsl(var(--text))]">
            <strong>ECG continu obligatoire :</strong> Surveillance scope pour hyperkaliémie et hypokaliémie
          </AlertDescription>
        </Alert>
      </div>

      {/* Main Content */}
      <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2 text-[hsl(var(--text))]">
            <Droplet className="w-5 h-5 text-blue-400" />
            Protocoles Métaboliques
          </CardTitle>
          <CardDescription>
            Sélectionnez une pathologie pour afficher le protocole détaillé
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[600px] pr-4">
            <Accordion type="single" collapsible className="w-full space-y-4">
              {pathologies.map((patho) => {
                const Icon = patho.icon;
                const isLoading = aiLoading[patho.id];
                const aiResult = aiResults[patho.id];

                return (
                  <AccordionItem
                    key={patho.id}
                    value={patho.id}
                    className="border border-[hsl(var(--border))] rounded-lg overflow-hidden bg-[hsl(var(--bg))]"
                  >
                    <AccordionTrigger
                      className="px-6 py-4 hover:no-underline hover:bg-[hsl(var(--bg-elev))] transition-colors"
                      data-testid={`accordion-trigger-${patho.id}`}
                    >
                      <div className="flex items-center gap-3 w-full">
                        <div className={`p-2 ${patho.bg} rounded-lg border ${patho.border}`}>
                          <Icon className={`w-5 h-5 ${patho.color}`} />
                        </div>
                        <span className="font-semibold text-[hsl(var(--text))] text-left">
                          {patho.name}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 py-4 space-y-4">
                      <div className="space-y-4">
                        {/* Contenu médical */}
                        <div className="prose prose-invert max-w-none">
                          <div className="text-sm text-[hsl(var(--text))] whitespace-pre-wrap leading-relaxed">
                            {patho.content.split('\n').map((line, idx) => {
                              if (line.startsWith('**') && line.endsWith('**')) {
                                return (
                                  <h3 key={idx} className="text-lg font-semibold text-cyan-400 mt-4 mb-2">
                                    {line.replace(/\*\*/g, '')}
                                  </h3>
                                );
                              }
                              if (line.startsWith('• ') || line.startsWith('- ')) {
                                return (
                                  <div key={idx} className="flex items-start gap-2 my-1">
                                    <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                                    <span>{line.substring(2)}</span>
                                  </div>
                                );
                              }
                              if (line.startsWith('⚠️')) {
                                return (
                                  <div key={idx} className="flex items-start gap-2 p-3 bg-orange-500/10 rounded-lg border border-orange-400/30 my-2">
                                    <AlertTriangle className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                                    <span>{line.substring(2)}</span>
                                  </div>
                                );
                              }
                              return <p key={idx} className="my-1">{line}</p>;
                            })}
                          </div>
                        </div>

                        {/* Bouton Analyse AI */}
                        <Button
                          onClick={() => handleAiAnalysis(patho.id, patho.name, patho.content)}
                          disabled={isLoading}
                          className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600"
                          data-testid={`ai-button-${patho.id}`}
                        >
                          {isLoading ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Analyse AI en cours...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4 mr-2" />
                              Analyse AI de ce protocole
                            </>
                          )}
                        </Button>

                        {/* Résultat AI */}
                        {aiResult && (
                          <div className="p-6 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-400/30 rounded-lg">
                            <h3 className="text-lg font-semibold text-cyan-400 mb-4 flex items-center gap-2">
                              <Sparkles className="w-5 h-5" />
                              Analyse AI
                            </h3>
                            <div className="prose prose-invert max-w-none">
                              <div className="text-[hsl(var(--text))] whitespace-pre-wrap text-sm" data-testid={`ai-result-${patho.id}`}>
                                {aiResult}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
